import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { Copy, RefreshCw, ExternalLink, Loader2, Power, Twitter, Linkedin, Check } from "lucide-react";

const LIMITS = { name: 120, description: 400, positioning: 400, contact: 200 };
const MAX_FEATURED = 3;

export default function PressKitPublic() {
  const { t, lang } = useLang();
  const [pk, setPk] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [positioning, setPositioning] = useState("");
  const [contact, setContact] = useState("");
  const [featured, setFeatured] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/press-kit-public/me"),
        api.get("/analyses"),
      ]);
      const d = r1.data;
      setPk(d);
      setName(d.name || "");
      setDescription(d.description || "");
      setPositioning(d.positioning || "");
      setContact(d.contact || "");
      setFeatured(d.featured_analysis_ids || []);
      setAnalyses(r2.data || []);
    } catch (e) {
      toast.error("load error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/press-kit-public/me", { name, description, positioning, contact, featured_analysis_ids: featured });
      setPk(r.data);
      toast.success("✓");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const toggle = async () => {
    setBusy(true);
    try { const r = await api.post("/press-kit-public/me/toggle"); setPk(r.data); toast.success(r.data.active ? (lang === "es" ? "Activado" : "Activated") : (lang === "es" ? "Desactivado" : "Deactivated")); }
    catch (e) { toast.error("Error"); }
    finally { setBusy(false); }
  };

  const regen = async () => {
    if (!window.confirm(lang === "es"
      ? "¿Regenerar token? La URL anterior dejará de funcionar inmediatamente."
      : "Regenerate token? The previous URL will stop working immediately.")) return;
    setBusy(true);
    try { const r = await api.post("/press-kit-public/me/regenerate-token"); setPk(r.data); toast.success("✓"); }
    catch (e) { toast.error("Error"); }
    finally { setBusy(false); }
  };

  const copyUrl = async () => {
    if (!pk?.public_url) return;
    await navigator.clipboard.writeText(pk.public_url);
    setCopied(true);
    toast.success("✓");
    setTimeout(() => setCopied(false), 1500);
  };

  const toggleFeatured = (id) => {
    setFeatured((curr) => {
      if (curr.includes(id)) return curr.filter((x) => x !== id);
      if (curr.length >= MAX_FEATURED) return curr;
      return [...curr, id];
    });
  };

  const shareX = () => {
    if (!pk?.active) return;
    const text = `${name || "GalascioDAS"} — ${description || ""}`.slice(0, 200);
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(pk.public_url)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareLinkedIn = () => {
    if (!pk?.active) return;
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pk.public_url)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!pk) return <div className="min-h-screen bg-[#FAFAFA]"><Header /><div className="max-w-[900px] mx-auto px-6 py-20 font-mono-tech text-xs uppercase tracking-widest">···</div></div>;

  const Counter = ({ v, max }) => (
    <span className="text-[#404040]">({v.length}/{max})</span>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="press-kit-management">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-10 md:py-16 space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[#171717] pb-6">
          <div>
            <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#D90429] mb-3">· PRESS KIT · PÚBLICO NO-LISTADO</div>
            <h1 className="font-serif-display text-4xl md:text-5xl font-bold leading-none tracking-tight">
              {lang === "es" ? "Gestión del press kit" : "Press kit management"}
            </h1>
          </div>
          <div className={`px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.3em] border ${pk.active ? "bg-[#D90429] text-white border-[#D90429]" : "border-[#0A0A0A] text-[#0A0A0A]"}`} data-testid="active-badge">
            {pk.active ? (lang === "es" ? "ACTIVO" : "ACTIVE") : (lang === "es" ? "INACTIVO" : "INACTIVE")}
          </div>
        </div>

        {/* URL block */}
        <section className="border border-[#171717] bg-white p-5" data-testid="url-block">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">
            {lang === "es" ? "URL pública" : "Public URL"}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <code data-testid="public-url" className="flex-1 min-w-0 font-mono-tech text-xs md:text-sm bg-[#FAFAFA] border border-[#E5E5E5] px-3 py-2 overflow-x-auto whitespace-nowrap">
              {pk.public_url}
            </code>
            <button data-testid="copy-url" onClick={copyUrl} className="px-3 py-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2">
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {lang === "es" ? "Copiar" : "Copy"}
            </button>
            <a data-testid="open-public" href={pk.active ? pk.public_url : "#"} target="_blank" rel="noopener noreferrer"
               onClick={(e) => !pk.active && e.preventDefault()}
               className={`px-3 py-2 border border-[#0A0A0A] font-mono-tech text-xs uppercase tracking-widest inline-flex items-center gap-2 ${pk.active ? "hover:bg-[#0A0A0A] hover:text-white" : "opacity-40 pointer-events-none"}`}>
              <ExternalLink className="w-3 h-3" /> {lang === "es" ? "Ver" : "View"}
            </a>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="toggle-active" onClick={toggle} disabled={busy} className={`px-4 py-2 font-mono-tech text-xs uppercase tracking-[0.25em] border inline-flex items-center gap-2 ${pk.active ? "border-[#D90429] text-[#D90429] hover:bg-[#D90429] hover:text-white" : "border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-[#D90429] hover:border-[#D90429]"}`}>
              <Power className="w-3 h-3" /> {pk.active ? (lang === "es" ? "Desactivar" : "Deactivate") : (lang === "es" ? "Activar" : "Activate")}
            </button>
            <button data-testid="regen-token" onClick={regen} disabled={busy} className="px-4 py-2 border border-[#0A0A0A] font-mono-tech text-xs uppercase tracking-[0.25em] hover:bg-[#0A0A0A] hover:text-white inline-flex items-center gap-2">
              <RefreshCw className="w-3 h-3" /> {lang === "es" ? "Regenerar token" : "Regenerate token"}
            </button>
            <button data-testid="share-x" onClick={shareX} disabled={!pk.active}
              className="px-4 py-2 border border-[#0A0A0A] font-mono-tech text-xs uppercase tracking-[0.25em] hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2">
              <Twitter className="w-3 h-3" /> X
            </button>
            <button data-testid="share-linkedin" onClick={shareLinkedIn} disabled={!pk.active}
              className="px-4 py-2 border border-[#0A0A0A] font-mono-tech text-xs uppercase tracking-[0.25em] hover:bg-[#0A0A0A] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2">
              <Linkedin className="w-3 h-3" /> LinkedIn
            </button>
          </div>
          {!pk.active && (
            <div
              data-testid="share-hint"
              className="mt-3 font-sans-body text-[11px] italic text-[#A3A3A3]"
            >
              {t("press_kit_share_hint")}
            </div>
          )}
        </section>

        {/* Editable fields */}
        <section className="border border-[#171717] bg-white p-6 space-y-6" data-testid="fields-block">
          <div>
            <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-1">
              {lang === "es" ? "Nombre" : "Name"} <Counter v={name} max={LIMITS.name} />
            </label>
            <input data-testid="field-name" type="text" maxLength={LIMITS.name} value={name} onChange={(e) => setName(e.target.value)} className="w-full border-b border-[#0A0A0A] bg-transparent px-0 py-2 text-lg focus:outline-none focus:border-[#D90429]" />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-1">
              {lang === "es" ? "Descripción" : "Description"} <Counter v={description} max={LIMITS.description} />
            </label>
            <textarea data-testid="field-description" maxLength={LIMITS.description} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border border-[#E5E5E5] focus:border-[#0A0A0A] px-3 py-2 text-base focus:outline-none resize-none" />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-1">
              {lang === "es" ? "Posicionamiento" : "Positioning"} <Counter v={positioning} max={LIMITS.positioning} />
            </label>
            <textarea data-testid="field-positioning" maxLength={LIMITS.positioning} rows={3} value={positioning} onChange={(e) => setPositioning(e.target.value)} className="w-full border border-[#E5E5E5] focus:border-[#0A0A0A] px-3 py-2 text-base focus:outline-none resize-none font-serif-display italic" />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-1">
              {lang === "es" ? "Contacto (email o URL)" : "Contact (email or URL)"} <Counter v={contact} max={LIMITS.contact} />
            </label>
            <input data-testid="field-contact" type="text" maxLength={LIMITS.contact} value={contact} onChange={(e) => setContact(e.target.value)} className="w-full border-b border-[#0A0A0A] bg-transparent px-0 py-2 text-base focus:outline-none focus:border-[#D90429]" />
          </div>

          {/* Featured analyses selector */}
          <div>
            <label className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">
              {lang === "es" ? "Análisis destacados" : "Featured analyses"} ({featured.length}/{MAX_FEATURED})
            </label>
            {analyses.length === 0 ? (
              <div className="font-mono-tech text-xs uppercase tracking-widest text-[#A3A3A3]">—</div>
            ) : (
              <ul className="space-y-2 max-h-[280px] overflow-y-auto subtle-scroll border border-[#E5E5E5] p-2" data-testid="featured-list">
                {analyses.map((a) => {
                  const sel = featured.includes(a.id);
                  return (
                    <li key={a.id} className={`flex items-center gap-3 p-2 border ${sel ? "border-[#D90429] bg-[#FFF5F5]" : "border-transparent hover:border-[#E5E5E5]"}`}>
                      <button data-testid={`feature-${a.id}`} onClick={() => toggleFeatured(a.id)} className={`w-5 h-5 border-2 inline-flex items-center justify-center shrink-0 ${sel ? "border-[#D90429] bg-[#D90429] text-white" : "border-[#0A0A0A]"}`}>
                        {sel && <Check className="w-3 h-3" />}
                      </button>
                      <span className="font-serif-display text-base leading-snug line-clamp-2 flex-1">{a.title}</span>
                      <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[#A3A3A3] shrink-0">{new Date(a.created_at).toLocaleDateString()}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex justify-end border-t border-[#E5E5E5] pt-4">
            <button data-testid="save-btn" onClick={save} disabled={busy} className="bg-[#D90429] text-white px-6 py-3 font-mono-tech text-xs uppercase tracking-[0.3em] border border-[#D90429] hover:bg-[#0A0A0A] hover:border-[#0A0A0A] disabled:opacity-40 inline-flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {lang === "es" ? "Guardar" : "Save"}
            </button>
          </div>
        </section>

        <div className="text-center"><Link to="/app" className="font-mono-tech text-[10px] uppercase tracking-[0.3em] hover:text-[#D90429]">← {lang === "es" ? "Volver al panel" : "Back to workspace"}</Link></div>
      </div>
    </div>
  );
}
